import React from 'react';
import moment from 'moment';
import { ui } from 'util/ui';
import { ImageIcon } from 'components/shell/Icons';

/**
 * The pop-up under the search box while typing. UI refresh styling (Aaron, 5 Nov 2026): a white
 * rounded panel that scrolls, rows with a small 16:9 thumbnail, the title with client underneath,
 * and the date on the right. Click a row to open that project; Enter filters the page instead.
 */

type Props = {
  items: IProject[];
  click(projectID: string): void;
};

export const SearchList: React.FC<Props> = ({ items, click }) => {
  if (!items.length) return null;
  return (
    <div
      role="listbox"
      aria-label="Matching projects"
      style={{
        position: 'absolute',
        top: 46,
        left: 0,
        right: 0,
        zIndex: 100,
        maxHeight: 'min(440px, 60vh)',
        overflowY: 'auto',
        background: ui.surface,
        border: `1px solid ${ui.line}`,
        borderRadius: 14,
        boxShadow: '0 18px 44px rgba(0,0,0,0.16)',
        padding: 6,
        fontFamily: ui.font,
      }}
    >
      {items.map((item) => (
        <div
          key={item.id}
          role="option"
          aria-selected={false}
          tabIndex={-1}
          className="archy-row-btn"
          onMouseDown={(e) => e.preventDefault() /* keep the search box focused until the click lands */}
          onClick={() => click(item.id)}
          style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '6px 8px', borderRadius: 10, cursor: 'pointer' }}
        >
          <span
            style={{
              width: 64,
              height: 36,
              flex: 'none',
              borderRadius: 7,
              background: item.featureURL ? `#24506B url("${item.featureURL}") center / cover` : '#24506B',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {!item.featureURL && <ImageIcon size={16} />}
          </span>
          <span style={{ flex: 1, minWidth: 0 }}>
            <span style={{ display: 'block', fontSize: 14, fontWeight: 600, color: ui.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {item.title || 'Untitled project'}
            </span>
            {(item.client || item.agency) && (
              <span style={{ display: 'block', fontSize: 12, color: ui.muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {[item.client, item.agency].filter(Boolean).join(' · ')}
              </span>
            )}
          </span>
          <span style={{ flex: 'none', fontSize: 12, color: ui.muted, fontVariantNumeric: 'tabular-nums' }}>
            {item.date ? moment.unix(Number(item.date)).format('D MMM YYYY') : ''}
          </span>
        </div>
      ))}
    </div>
  );
};
